import React, { ComponentPropsWithRef, ElementRef, useMemo, useRef, useState } from 'react';
import { Modal, ModalBody, ModalCloseBtn, ModalContent, ModalFooter, ModalHeader } from '@fiverr-private/overlay';
import { Typography } from '@fiverr-private/typography';
import { Button } from '@fiverr-private/button';
import { Combobox, ComboboxOption, ComboboxOptionLabel } from '@fiverr-private/forms';
import { Stack } from '@fiverr-private/layout_components';
import { appTranslate } from '../../i18n';
import { getCountriesOptions } from './helpers';

type Values = ComponentPropsWithRef<typeof Combobox>['value'];

interface SelectCountryModalProps {
    isOpenModal: boolean;
    countries: string[];
    onCloseModal: () => void;
    onSelectCountry: (countryCode: string) => void;
    defaultCountry?: string;
}

export const SelectCountryModal = ({
    countries,
    isOpenModal,
    onCloseModal,
    onSelectCountry,
    defaultCountry = 'US',
}: SelectCountryModalProps) => {
    const selectRef = useRef<ElementRef<typeof Combobox>>(null);

    const [country, setCountry] = useState<Values>({ item: defaultCountry });
    const countriesOptions = useMemo(() => getCountriesOptions(countries), [countries]);

    const handleUpdate = () => {
        if (country && typeof country.item === 'string') {
            onSelectCountry(country.item);
        }

        onCloseModal();
    };

    const handleFocus = () => {
        selectRef.current?.focus();
    };

    return (
        <Modal isOpen={isOpenModal} fullScreenBreakpoint="sm" onClose={onCloseModal}>
            <ModalContent size="sm">
                <ModalHeader>
                    {appTranslate('select_country_modal.title')}
                    <ModalCloseBtn />
                </ModalHeader>
                <ModalBody>
                    <Stack direction="column" rowGap="8">
                        <Typography color="grey_1000">{appTranslate('select_country_modal.description')}</Typography>

                        <Stack display="flex" direction="column" rowGap="2" paddingY="0.5">
                            <Typography as="span" color="grey_1200" fontWeight="semibold" onClick={handleFocus}>
                                {appTranslate('select_country_modal.fields.country.label')}
                            </Typography>

                            <Combobox
                                defaultValue={{
                                    item: defaultCountry,
                                }}
                                value={country}
                                onChange={setCountry}
                                placeholder={appTranslate<string>('select_country_modal.fields.country.placeholder')}
                                ref={selectRef}
                                appendToBody
                                disableFlip
                            >
                                {countriesOptions.map((option) => (
                                    <ComboboxOption value={option.value} key={option.value}>
                                        <Stack>
                                            {option.flagIcon}
                                            <ComboboxOptionLabel>{option.label}</ComboboxOptionLabel>
                                        </Stack>
                                    </ComboboxOption>
                                ))}
                            </Combobox>
                        </Stack>
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={handleUpdate}>{appTranslate('select_country_modal.actions.update')}</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
